import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { HttpResponsesHandlersFactory, IHttpResponseHandlerSettings } from "@bbwt/modules/data-service/http-responses-handler";
import { BaseDataService } from "../../bbwt/modules/data-service";
import { PatientSettings } from "../../main/system-configuration";
import { RegisterPatient } from "../models/im1/register-patient";
import { PatientLimitedNhsAccessInfo } from "@project/models/im1/patient-limited-nhs-access-info";
import { PatientIm1RegistrationInfo } from "@project/models/im1/patient-im1-registration-info";
import { AppStorage } from "@bbwt/utils/app-storage";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { FileDetails } from "@main/file-storage";

@Injectable({
    providedIn: "root"
})
export class IM1Service extends BaseDataService {
    readonly url = "api/im1";

    readonly emis = "Emis";
    readonly emisNormalized = "EMIS";
    readonly tpp = "Tpp";
    readonly tppNormalized = "TPP";

    constructor(http: HttpClient, handlersFactory: HttpResponsesHandlersFactory, private patientService: PatientService) {
        super(http, handlersFactory);
    }

    registerWithPin(data: RegisterPatient): Promise<void> {
        return this.handleRequest<void>(this.http.post<void>(`${this.url}/register-with-pin`, data), this.handlersFactory.getForReadAll());
    }

    unregisterPatient(patientId: string): Promise<void> {
        return this.handleRequest<void>(
            this.http.post<void>(`${this.url}/${patientId}/unregister`, null),
            this.handlersFactory.getForReadAll()
        );
    }

    async isRegistered() {
        const storedData = JSON.parse(AppStorage.getItem(AppStorage.IsRegisteredErrorMessage));

        if(storedData?.patientId === this.patientService.currentPatientId && storedData?.errorMessage?.includes("We're sorry, we could not connect to the NHS Service. Please try again later")){
            return {
                isRegistered: true,
                errorMessage: storedData.errorMessage
            };
        }

        const response = await this.handleRequest(
            this.http.get<PatientIm1RegistrationInfo>(`${this.url}/is-registered`),
            this.handlersFactory.getDefault()
        );

        AppStorage.setItem(AppStorage.IsRegisteredErrorMessage, JSON.stringify({
            errorMessage: response.errorMessage,
            patientId: this.patientService.currentPatientId
          }));

        return response;
    }

    isRegisteredSelectedPatient(patientId: string) {
        return this.handleRequest(
            this.http.get<PatientIm1RegistrationInfo>(`${this.url}/${patientId}/is-registered/`),
            this.handlersFactory.getForReadAll()
        );
    }

    getSettings(patientId: string): Promise<PatientSettings> {
        return this.handleRequest<any>(this.http.get<any>(`${this.url}/${patientId}/settings/`), this.handlersFactory.getForReadAll());
    }

    getMedications(patientId: string): Promise<any> {
        return this.handleRequest<any>(this.http.get<any>(`${this.url}/${patientId}/medications`), this.handlersFactory.getForReadAll());
    }

    getAllergies(patientId: string): Promise<any> {
        return this.handleRequest<any>(this.http.get<any>(`${this.url}/${patientId}/allergies`), this.handlersFactory.getForReadAll());
    }

    async patientHasLimitedAccess() {
       const response = await this.handleRequest(
            this.http.get<PatientLimitedNhsAccessInfo>(`${this.url}/limited-access`),
            this.handlersFactory.getForReadAll()
        );

        if(response?.errorMessage === null || response?.errorMessage.includes("We're sorry, we could not connect to the NHS Service. Please try again later")){
         AppStorage.setItem(AppStorage.IsRegisteredErrorMessage, JSON.stringify({
            errorMessage: response.errorMessage,
            patientId: this.patientService.currentPatientId
          }));
        }

        return response;
    }

    getLastAccessRequestedDate(patientId: string): Promise<string> {
        return this.handleRequest<string>(
            this.http.get<string>(`${this.url}/${patientId}/last-requested-access-date`),
            this.handlersFactory.getForReadAll()
        );
    }

    getIM1SystemNormalized(patientId: string): Promise<string> {
        return this.handleRequest<string>(
            this.http.get<string>(`${this.url}/${patientId}/get-im1-system`),
            this.handlersFactory.getForReadAll()
        );
    }

    async updateLastAccessRequestedDate() {
        return await this.handleRequest(
            this.http.post<void>(`${this.url}/update-last-requested-access-date`, null),
            this.handlersFactory.getForUpdate("updateLastAccessRequestedDate", { showSuccessMessage: false })
        );
    }

    async generateSubjectAccessRequestPdf(): Promise<FileDetails> {
        return this.handleRequest(
            this.http.get<FileDetails>(
                `${this.url}/subject-access-request-pdf`,
            )
        );
    }

    getMedicalRecord(
        patientId: string,
        request: {
            dateFrom: any;
            dateTo: any;
            itemType: string;
        }
    ): Promise<any> {
        return this.handleRequest<any>(
            this.http.post<any>(`${this.url}/${patientId}/medical-record`, request),
            this.handlersFactory.getForReadAll()
        );
    }

    getDocumentFiles(
        patientId: string,
        documentId: string,
        responseHandlerSettings?: IHttpResponseHandlerSettings,
        extension?: string
    ): Promise<any> {
        return this.handleRequest<any>(
            this.http.post<any>(`${this.url}/${patientId}/document-files`, { documentId, extension }),
            this.handlersFactory.getForReadAll(responseHandlerSettings)
        );
    }

    getPracticeSettings(patientId: string): Promise<any> {
        return this.handleRequest<any>(
            this.http.get<any>(`${this.url}/${patientId}/practice-settings`),
            this.handlersFactory.getForReadAll()
        );
    }

    requestMedicalRecordAccess() {
        return this.handleRequest(
            this.http.post<void>(`${this.url}/request-medical-record-access`, null),
            this.handlersFactory.getForReadAll()
        );
    }

    async deleteAllPatientsIM1DataFromOrganisation(organizationId: string) {
        return await this.handleRequest<any>(
            this.http.post<any>(`${this.url}/delete-organization-data/${organizationId}`, null),
            this.handlersFactory.getForUpdate("entityTitle", { showSuccessMessage: false })
        );
    }

    getTestResultsList(
        patientId: string,
        request: {
            dateFrom: any;
            dateTo: any;
        }
    ): Promise<any> {
        return this.handleRequest<any>(
            this.http.post<any>(`${this.url}/${patientId}/test-results-list`, request),
            this.handlersFactory.getForReadAll()
        );
    }

    getTestResultsDetails(
        patientId: string,
        request: {
            dateFrom: any;
            dateTo: any;
            testResultId: string;
        }
    ): Promise<any> {
        return this.handleRequest<any>(
            this.http.post<any>(`${this.url}/${patientId}/test-results-details`, request),
            this.handlersFactory.getForReadAll()
        );
    }

    async deletePatientIM1Data(patientId: string) {
        return await this.handleRequest<any>(
            this.http.post<any>(`${this.url}/delete-patient-data/${patientId}`, null),
            this.handlersFactory.getForUpdate("entityTitle", { showSuccessMessage: false })
        );
    }

    async updateDatabase(): Promise<void> {
        return this.handleRequest<void>(this.http.post<void>(`${this.url}/update-database`, null), this.handlersFactory.getForReadAll());
    }

    async isEmis(patientId: string): Promise<boolean> {
        return this.handleRequest<any>(this.http.get<any>(`${this.url}/${patientId}/is-emis`), this.handlersFactory.getForReadAll());
    }

    async updateIM1DataResearchOptOut(patientId: string, nhsModuleEnable: boolean, researchOptOut: boolean): Promise<void> {
        return this.handleRequest<void>(
            this.http.post<void>(`${this.url}/${patientId}/update-im1-data-research-opt-out/${nhsModuleEnable}`, researchOptOut),
            this.handlersFactory.getForReadAll()
        );
    }

    async updateIM1DataResearchOptOutByOrganization(organizationId: string, nhsModuleEnable: boolean, researchOptOut: boolean): Promise<void> {
        return this.handleRequest<void>(
            this.http.post<void>(`${this.url}/${organizationId}/update-im1-data-research-opt-out-org/${nhsModuleEnable}`, researchOptOut),
            this.handlersFactory.getForReadAll()
        );
    }
}
